import { useTranslation } from "react-i18next";
import { type LinkedDocument } from "~/api/types";
import { useTiers } from "~/api/swr/useTiers";
import { Link } from "react-router";
import { Button } from "~/atoms/button";
import { Label } from "~/atoms/label";
import { getNoTierLabel, isUntiered } from "~/helpers/tier";

interface Props {
  linkedDocuments: LinkedDocument[];
  onLinkClick?: () => void;
}

const DocumentLinks = ({ linkedDocuments, onLinkClick }: Props) => {
  const { data: tiers, error } = useTiers();
  const { t } = useTranslation();

  if (error) return <div>{t(($) => $.toasts.error.generic)}</div>;
  if (tiers === undefined) return null;

  if (linkedDocuments.length === 0) {
    return (
      <div className="text-muted-foreground text-xs italic">
        {t(($) => $.sidebar.linkedNone)}
      </div>
    );
  }

  const tierIds = new Set(tiers.map((tier) => tier.id));
  const untieredDocuments = linkedDocuments.filter((doc) =>
    isUntiered(doc.tierId, tierIds),
  );

  return (
    <div className="space-y-4 text-sm">
      {tiers.map((tier) => {
        const documents = linkedDocuments.filter(
          (doc) => doc.tierId === tier.id,
        );
        if (documents.length === 0) return null;

        return (
          <DocumentList
            key={tier.id}
            tierTitle={tier.title}
            documents={documents}
            onLinkClick={onLinkClick}
          />
        );
      })}
      {untieredDocuments.length > 0 && (
        <DocumentList
          tierTitle={getNoTierLabel(t)}
          documents={untieredDocuments}
          onLinkClick={onLinkClick}
        />
      )}
    </div>
  );
};

interface DocumentListProps {
  tierTitle: string;
  documents: LinkedDocument[];
  onLinkClick?: () => void;
}

const DocumentList = ({
  tierTitle,
  documents,
  onLinkClick,
}: DocumentListProps) => {
  return (
    <div>
      <Label variant="muted">{tierTitle}</Label>
      <ul className="flex min-w-0 list-disc flex-col items-start pl-5">
        {documents.map(({ id, title }) => (
          <li key={id} className="w-full">
            <Button
              variant="link"
              asChild
              onClick={onLinkClick}
              className="h-auto p-0"
            >
              <Link
                to={`/documents/${id}`}
                className="w-full justify-start wrap-break-word whitespace-normal!"
              >
                {title}
              </Link>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DocumentLinks;
